import { gcApi } from 'config/axios';
import { call, fork, put, select, takeLatest } from 'redux-saga/effects';

// utils
import PrivacyAlertUtil from 'utils/PrivacyAlertUtil';
import { groupRequestErrors } from 'utils/groups/GroupsUtil';
import UnreadBubbleDispatcher from 'backbone/events/UnreadBubbleDispatcher';
import { toQueryString } from 'utils/UrlUtil';
// utils -- userSocialProfileImage
import { PROFILE_IMAGE_VERSION } from 'utils/userSocialProfileImage/profileImageConstantsV2';

import { getUserPreferenceValue, getUserPreferenceFinish } from 'reducers/userPreference';
import { fetchUserPreference, setUserPreference, fetchSystemPreference } from 'actions/index';
import { groupInviteSpammingDaysKey } from 'models/preferences/PreferencesMapping';

import {
    INBOX_PAGE_CHALLENGE_REQ_ACTION,
    INBOX_PAGE_JOIN_GROUP_CONSENT_DIALOG_ACTION,
    INBOX_PAGE_CONNECTION_REQ_ACTION,
    INBOX_PAGE_FETCH_REQUESTS,
    INBOX_PAGE_GROUP_REQ_ACTION,
    INBOX_PAGE_DATA_PRIVACY_DIALOG_ACTION,
    INBOX_PAGE_CHALLENGE_DIALOG_ACTION,
    INBOX_PAGE_INIT,
    INBOX_PAGE_DEVICE_REQ_ACTION,
    INBOX_PAGE_DELETE_GROUP_REQ_DIALOG_ACTION
} from 'actions/ActionTypes';
import {
    inboxPageFetchRequestsDone,
    inboxPageSetDataPrivacyDialogOpen,
    inboxPageSetJoinGroupConsentDialogOpen,
    inboxPageSetGroupErrorDialogOpen,
    inboxPageSetChallengeDialogOpen,
    inboxPageSetRequestStatus,
    inboxPageSetDeleteGroupRequestDialogOpen
} from 'actions/inboxPageActions';

import {
    selectViewerSocialProfile,
    selectViewerUserPreferences,
    selectDataPrivacyDialogState,
    selectJoinGroupConsentDialogState,
    selectChallengeDialogState,
    selectUseFallback,
    selectRequestStatus,
    selectDeleteGroupRequestDialogState
} from '../reducers/inboxPage/inboxPage';

import {
    CHALLENGE_DIALOG_TYPE,
    CHALLENGE_TYPE,
    DATA_PRIVACY_ACTION,
    REQUEST_KEY,
    REQUEST_STATUS
} from '../InboxPage/constants';
import '../InboxPage/typedefs';

const reqIds = [
    REQUEST_KEY.CONNECTION_REQUEST,
    REQUEST_KEY.GROUP_JOIN_REQUEST,
    REQUEST_KEY.GROUP_INVITE,
    REQUEST_KEY.ADHOC_INVITE,
    REQUEST_KEY.KIDADHOC_INVITE,
    REQUEST_KEY.KIDTEAM_INVITE,
    REQUEST_KEY.DEVICE_INVITE
];

const CHALLENGE_PRIVACY_DIMISSED_PREF_KEY = 'ChallengePrivacy.Dismissed';

export function* inboxPageInitSaga() {
    yield put(fetchSystemPreference(groupInviteSpammingDaysKey));

    const gdprDismissed = yield select(getUserPreferenceFinish, 'GDPR.ProfileActivityVisibility.Dismissed');
    if (!gdprDismissed) {
        yield put(fetchUserPreference('GDPR.ProfileActivityVisibility.Dismissed'));
    }

    const useFallback = yield select(selectUseFallback);
    if (!useFallback) {
        const challengePrivacyDismiss = yield select(getUserPreferenceFinish, CHALLENGE_PRIVACY_DIMISSED_PREF_KEY);
        if (!challengePrivacyDismiss) {
            yield put(fetchUserPreference(CHALLENGE_PRIVACY_DIMISSED_PREF_KEY));
        }
    }
}

export function* inboxPageFetchRequestsSaga(action) {
    const { cursor, limit } = action;
    try {
        const params = {
            limit
        };
        if (cursor) {
            params.cursor = cursor;
        }
        params['req'] = reqIds;
        params['image-version'] = Object.values(PROFILE_IMAGE_VERSION);
        const querystring = toQueryString(params);
        /** this endpoint automatically set `requestView` to true */
        const response = yield call(gcApi.get, `/web-gateway/inbox/v2/requests${querystring}`);
        yield put(
            inboxPageFetchRequestsDone({
                requests: response.data.requests,
                nextCursor: response.data.nextCursor,
                profileInfos: response.data.profileInfos
            })
        );
    } catch (error) {
        yield put(inboxPageFetchRequestsDone({ error: true }));
    }
    /**
     * when entering inbox page, unread bubble dot update is disabled
     * we turn it back after request api called
     * */
    UnreadBubbleDispatcher.trigger(UnreadBubbleDispatcher.Events.ENABLE);
}

export function* inboxPageConnectionRequestAcceptSaga(action) {
    const viewerSocialProfile = yield select(selectViewerSocialProfile);
    const viewerPreferences = yield select(selectViewerUserPreferences);
    const { uniqueKey, request } = action;
    try {
        yield call(gcApi.put, `/connection-service/connection/accept/${request.connectionRequestId}`);
        yield put(inboxPageSetRequestStatus({ uniqueKey, status: REQUEST_STATUS.ACCEPTED }));
        PrivacyAlertUtil.renderPrivacyAlert(viewerSocialProfile, viewerPreferences, 'connection');
    } catch (error) {
        yield put(inboxPageSetRequestStatus({ uniqueKey, status: REQUEST_STATUS.UNDECIDED }));
    }
}

export function* inboxPageConnectionRequestActionSaga(action) {
    const gdprDismissed = yield select(getUserPreferenceValue, 'GDPR.ProfileActivityVisibility.Dismissed');

    const { uniqueKey, accept } = action;

    /**
     * @type {TConnectionRequest}
     */
    const request = action.request;

    yield put(inboxPageSetRequestStatus({ uniqueKey, status: REQUEST_STATUS.PROCESSING }));

    if (accept) {
        if (gdprDismissed === 'true') {
            yield fork(inboxPageConnectionRequestAcceptSaga, action);
        } else {
            yield put(inboxPageSetDataPrivacyDialogOpen({ open: true, uniqueKey, request }));
        }
        return;
    }

    try {
        yield call(gcApi.put, `/userprofile-service/connection/decline/${request.connectionRequestId}`);
        yield put(inboxPageSetRequestStatus({ uniqueKey, status: REQUEST_STATUS.DECLINED }));
    } catch (error) {
        yield put(inboxPageSetRequestStatus({ uniqueKey, status: REQUEST_STATUS.UNDECIDED }));
    }
}

export function* inboxPageDataPrivacyDialogActionSaga(action) {
    const { actionType } = action;

    const { uniqueKey, request } = yield select(selectDataPrivacyDialogState);

    /** close consent dialog */
    yield put(inboxPageSetDataPrivacyDialogOpen({ open: false }));

    if (actionType === DATA_PRIVACY_ACTION.CANCEL) {
        /** cancel action */
        yield put(inboxPageSetRequestStatus({ uniqueKey, status: REQUEST_STATUS.UNDECIDED }));
        return;
    }

    if (actionType === DATA_PRIVACY_ACTION.DO_NOT_SHOW_AGAIN) {
        /** save preference */
        yield put(setUserPreference('GDPR.ProfileActivityVisibility.Dismissed', 'true'));
    }

    yield fork(inboxPageConnectionRequestAcceptSaga, { uniqueKey, request });
}

export function* inboxPageChallengeRequestAcceptSaga(action) {
    const useFallback = yield select(selectUseFallback);

    const { doNotShowAgain, uniqueKey } = action;

    /**
     * @type {TChallengeRequest}
     */
    const request = action.request;

    if (!useFallback && doNotShowAgain) {
        /** save preference */
        const oldDismissed = yield select(getUserPreferenceValue, CHALLENGE_PRIVACY_DIMISSED_PREF_KEY);
        let newDismissed = oldDismissed ? JSON.parse(oldDismissed) : [];

        if (!newDismissed.includes(request.challengeTypeId)) {
            newDismissed.push(request.challengeTypeId);
        }

        yield put(setUserPreference(CHALLENGE_PRIVACY_DIMISSED_PREF_KEY, JSON.stringify(newDismissed)));
    }

    try {
        let url;

        switch (request.challengeTypeId) {
            case CHALLENGE_TYPE.VIVOKID:
                url = `/vivokidchallenge-service/adHocChallenge/invite/${request.challengeInviteId}/accept`;
                break;
            case CHALLENGE_TYPE.VIVOKID_TEAMS:
                url = `/vivokidchallenge-service/teamChallenge/invite/${request.challengeInviteId}/accept`;
                break;
            default:
                url = `/adhocchallenge-service/adHocChallenge/invite/${request.challengeInviteId}/accept`;
                break;
        }
        yield call(gcApi.put, url);

        yield put(inboxPageSetRequestStatus({ uniqueKey, status: REQUEST_STATUS.ACCEPTED }));
    } catch (error) {
        yield put(inboxPageSetRequestStatus({ uniqueKey, status: REQUEST_STATUS.UNDECIDED }));
    }
}

export function* inboxPageChallengeRequestDeclineSaga(action) {
    const { uniqueKey } = action;

    /**
     * @type {TChallengeRequest}
     */
    const request = action.request;

    try {
        let url;

        switch (request.challengeTypeId) {
            case CHALLENGE_TYPE.VIVOKID:
                url = `/vivokidchallenge-service/adHocChallenge/invite/${request.challengeInviteId}/decline`;
                break;
            case CHALLENGE_TYPE.VIVOKID_TEAMS:
                url = `/vivokidchallenge-service/teamChallenge/invite/${request.challengeInviteId}/decline`;
                break;
            default:
                url = `/adhocchallenge-service/adHocChallenge/invite/${request.challengeInviteId}/decline`;
                break;
        }
        yield call(gcApi.put, url);

        yield put(inboxPageSetRequestStatus({ uniqueKey, status: REQUEST_STATUS.DECLINED }));
    } catch (error) {
        yield put(inboxPageSetRequestStatus({ uniqueKey, status: REQUEST_STATUS.UNDECIDED }));
    }
}

export function* inboxPageChallengeDialogActionSaga(action) {
    const { ok, doNotShowAgain } = action;

    const challengeDialogState = yield select(selectChallengeDialogState);

    const { uniqueKey, dialogType } = challengeDialogState;

    /**
     * @type {TChallengeRequest}
     */
    const request = challengeDialogState.request;

    /** close dialog */
    yield put(inboxPageSetChallengeDialogOpen({ open: false }));

    if (!ok) {
        /** cancel action */
        yield put(inboxPageSetRequestStatus({ uniqueKey, status: REQUEST_STATUS.UNDECIDED }));
        return;
    }

    if (dialogType === CHALLENGE_DIALOG_TYPE.CANCEL_CONFIRM) {
        yield fork(inboxPageChallengeRequestDeclineSaga, { uniqueKey, request });
        return;
    }

    yield fork(inboxPageChallengeRequestAcceptSaga, { uniqueKey, request, doNotShowAgain });
}

export function* inboxPageChallengeRequestActionSaga(action) {
    const { uniqueKey, accept } = action;

    /**
     * @type {TChallengeRequest}
     */
    const request = action.request;

    yield put(inboxPageSetRequestStatus({ uniqueKey, status: REQUEST_STATUS.PROCESSING }));

    if (!accept) {
        yield put(
            inboxPageSetChallengeDialogOpen({
                open: true,
                dialogType: CHALLENGE_DIALOG_TYPE.CANCEL_CONFIRM,
                uniqueKey,
                request
            })
        );
        return;
    }

    const useFallback = yield select(selectUseFallback);

    if (useFallback) {
        yield fork(inboxPageChallengeRequestAcceptSaga, { uniqueKey, request });
        return;
    }

    let disimissList = [];

    try {
        const dismissedString = yield select(getUserPreferenceValue, CHALLENGE_PRIVACY_DIMISSED_PREF_KEY);
        if (typeof dismissedString === 'string') {
            disimissList = JSON.parse(dismissedString);
        }
    } catch (e) {
        /** ignore */
    }

    if (disimissList.includes(request.challengeTypeId)) {
        yield fork(inboxPageChallengeRequestAcceptSaga, { uniqueKey, request });
        return;
    }

    yield put(
        inboxPageSetChallengeDialogOpen({
            open: true,
            dialogType: CHALLENGE_DIALOG_TYPE.PRIVACY_CONFIRM,
            uniqueKey,
            request
        })
    );
}

export function* inboxPageGroupRequestActionSaga(action) {
    const { uniqueKey, accept } = action;

    /**
     * @type {TGroupRequest}
     */
    const request = action.request;

    yield put(inboxPageSetRequestStatus({ uniqueKey, status: REQUEST_STATUS.PROCESSING }));

    if (accept) {
        /** open consent dialog and wait for confirm/cancel */
        yield put(inboxPageSetJoinGroupConsentDialogOpen({ open: true, uniqueKey, request }));
        return;
    }

    if (request.groupVisibility === 'HIDDEN') {
        /** open confirm dialog and wait for confirm/cancel */
        yield put(inboxPageSetDeleteGroupRequestDialogOpen({ open: true, uniqueKey, request }));
        return;
    }

    yield put(inboxPageSetDeleteGroupRequestDialogOpen({ open: false, uniqueKey, request }));
    yield fork(inboxPageDeleteGroupRequestDialogActionSaga, { confirm: true });
}

export function* inboxPageDeleteGroupRequestDialogActionSaga(action) {
    const { confirm } = action;
    const { uniqueKey, request } = yield select(selectDeleteGroupRequestDialogState);

    /** close consent dialog */
    yield put(inboxPageSetDeleteGroupRequestDialogOpen({ open: false }));

    if (!confirm) {
        /** cancel delete */
        yield put(inboxPageSetRequestStatus({ uniqueKey, status: REQUEST_STATUS.UNDECIDED }));
        return;
    }

    try {
        if (request.groupInviteId) {
            yield call(gcApi.delete, `/group-service/invite/group/${request.groupId}`, {
                data: {
                    userGroupId: request.groupId,
                    groupInviteId: request.groupInviteId
                }
            });
        } else if (request.groupMemberRequestId) {
            yield call(gcApi.delete, `/group-service/private/group/${request.groupId}`, {
                data: {
                    userGroupId: request.groupId,
                    groupMemberRequestId: request.groupMemberRequestId
                }
            });
        }
        yield put(inboxPageSetRequestStatus({ uniqueKey, status: REQUEST_STATUS.DECLINED }));
    } catch (error) {
        if (error.response.status === 400 || error.response.status === 403 || error.response.status === 404) {
            // special design on API side for FE to take it as declined successfully for these error code
            yield put(inboxPageSetRequestStatus({ uniqueKey, status: REQUEST_STATUS.DECLINED }));
        } else {
            yield put(inboxPageSetRequestStatus({ uniqueKey, status: REQUEST_STATUS.UNDECIDED }));
        }
    }
}

export function* inboxPageJoinGroupConsentDialogActionSaga(action) {
    const { confirm } = action;

    const { uniqueKey, request } = yield select(selectJoinGroupConsentDialogState);

    /** close consent dialog */
    yield put(inboxPageSetJoinGroupConsentDialogOpen({ open: false }));

    if (!confirm) {
        /** decline join */
        yield put(inboxPageSetRequestStatus({ uniqueKey, status: REQUEST_STATUS.UNDECIDED }));
        return;
    }

    try {
        let response;
        if (request.groupInviteId) {
            response = yield call(gcApi.put, `/group-service/invite/group/${request.groupId}`, {
                userGroupId: request.groupId,
                groupInviteId: request.groupInviteId
            });
        } else if (request.groupMemberRequestId) {
            response = yield call(gcApi.put, `/group-service/private/group/${request.groupId}`, {
                userGroupId: request.groupId,
                groupMemberRequestId: request.groupMemberRequestId
            });
        }

        // response.data would be either true or false
        if (response.data) {
            yield put(inboxPageSetRequestStatus({ uniqueKey, status: REQUEST_STATUS.ACCEPTED }));
        } else {
            // special design on API side, the status code 200 with false as the response body,
            // which is the case that a requester had canceled the request
            yield put(inboxPageSetRequestStatus({ uniqueKey, status: REQUEST_STATUS.UNDECIDED }));
            yield put(
                inboxPageSetGroupErrorDialogOpen({
                    open: true,
                    errorType: !!request.groupInviteId
                        ? groupRequestErrors.invitationCancelled
                        : groupRequestErrors.requestCancelled
                })
            );
        }
    } catch (e) {
        yield put(inboxPageSetRequestStatus({ uniqueKey, status: REQUEST_STATUS.UNDECIDED }));

        if (e.response && (e.response.status === 403 || e.response.status === 404)) {
            const errorType =
                e.response.status === 403
                    ? groupRequestErrors.acceptRequestNoPermission
                    : groupRequestErrors.groupDeleted;
            yield put(inboxPageSetGroupErrorDialogOpen({ open: true, errorType }));
        }
    }
}

export function* inboxPageDeviceRequestActionSaga(action) {
    /** currently, we only support decline, accept means we navigate to get started page */

    const { uniqueKey, newStatus } = action;

    const prevRequestStatus = yield select(selectRequestStatus, uniqueKey);

    /**
     * @type {TDeviceRequest}
     */
    const request = action.request;

    const newRequestStatus = newStatus === 'DECLINED' ? REQUEST_STATUS.DECLINED : REQUEST_STATUS.UNDECIDED;

    yield put(
        inboxPageSetRequestStatus({
            uniqueKey,
            status: newStatus === 'DECLINED' ? REQUEST_STATUS.PROCESSING : REQUEST_STATUS.UNDOING
        })
    );

    try {
        let payload = {
            ...request,
            deviceInviteStatus: newStatus
        };
        if (payload.inviteeEmail === null) {
            payload.inviteeEmail = undefined;
        }
        if (payload.productPartNumber) {
            payload.productPartNumber = undefined;
        }
        yield call(gcApi.put, `/invite-service/invite/${request.deviceInviteId}`, payload);
        yield put(inboxPageSetRequestStatus({ uniqueKey, status: newRequestStatus }));
    } catch (error) {
        yield put(inboxPageSetRequestStatus({ uniqueKey, status: prevRequestStatus }));
    }
}

export const inboxPageSagas = [
    takeLatest(INBOX_PAGE_INIT, inboxPageInitSaga),
    takeLatest(INBOX_PAGE_FETCH_REQUESTS, inboxPageFetchRequestsSaga),
    takeLatest(INBOX_PAGE_CONNECTION_REQ_ACTION, inboxPageConnectionRequestActionSaga),
    takeLatest(INBOX_PAGE_CHALLENGE_REQ_ACTION, inboxPageChallengeRequestActionSaga),
    takeLatest(INBOX_PAGE_GROUP_REQ_ACTION, inboxPageGroupRequestActionSaga),
    takeLatest(INBOX_PAGE_DEVICE_REQ_ACTION, inboxPageDeviceRequestActionSaga),
    takeLatest(INBOX_PAGE_JOIN_GROUP_CONSENT_DIALOG_ACTION, inboxPageJoinGroupConsentDialogActionSaga),
    takeLatest(INBOX_PAGE_DATA_PRIVACY_DIALOG_ACTION, inboxPageDataPrivacyDialogActionSaga),
    takeLatest(INBOX_PAGE_CHALLENGE_DIALOG_ACTION, inboxPageChallengeDialogActionSaga),
    takeLatest(INBOX_PAGE_DELETE_GROUP_REQ_DIALOG_ACTION, inboxPageDeleteGroupRequestDialogActionSaga)
];
